import { useState } from 'react';
import { 
  ArrowUpDown, ArrowUp, ArrowDown, Globe, AlertCircle, 
  Edit2, Check, X, MoreHorizontal
} from 'lucide-react';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const statusColors = {
  'Active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Paused': 'bg-amber-50 text-amber-700 border-amber-200',
  'Cancelled': 'bg-slate-100 text-slate-600 border-slate-200',
  'Lead': 'bg-blue-50 text-blue-700 border-blue-200',
};

const paymentStatusColors = {
  'Up to date': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Overdue': 'bg-red-50 text-red-700 border-red-200',
  'Unknown': 'bg-slate-100 text-slate-500 border-slate-200',
};

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
  return sortOrder === 'asc' 
    ? <ArrowUp className="w-3.5 h-3.5 ml-1" /> 
    : <ArrowDown className="w-3.5 h-3.5 ml-1" />;
};

export const ClientTable = ({ 
  clients, 
  packages, 
  isLoading, 
  onClientClick, 
  onClientUpdate,
  sortBy,
  sortOrder,
  onSort 
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const getPackageName = (client) => {
    // First check for subscription name from GoCardless
    if (client.subscription_name) {
      return client.subscription_name;
    }
    // Then check for assigned package
    if (client.package_id) {
      const pkg = packages.find(p => p.id === client.package_id);
      return pkg?.name || '-';
    }
    return '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isRenewalSoon = (dateStr) => {
    if (!dateStr) return false;
    const renewalDate = new Date(dateStr);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  const startEdit = (clientId, field, value, e) => {
    e.stopPropagation();
    setEditingCell({ clientId, field });
    setEditValue(value || '');
  };

  const saveEdit = async (clientId, e) => {
    e?.stopPropagation();
    if (editingCell) {
      await onClientUpdate(clientId, { [editingCell.field]: editValue });
      setEditingCell(null);
      setEditValue('');
    }
  };

  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e, clientId) => {
    if (e.key === 'Enter') {
      saveEdit(clientId, e);
    } else if (e.key === 'Escape') {
      cancelEdit(e);
    }
  };

  const columns = [
    { key: 'client_name', label: 'Client Name', sortable: true, width: 'w-[200px]' },
    { key: 'status', label: 'Status', sortable: true, width: 'w-[100px]' },
    { key: 'package', label: 'Package', sortable: false, width: 'w-[100px]' },
    { key: 'discount', label: 'Discount', sortable: false, width: 'w-[90px]' },
    { key: 'payment_method', label: 'Payment', sortable: true, width: 'w-[110px]' },
    { key: 'billing_frequency', label: 'Billing', sortable: true, width: 'w-[100px]' },
    { key: 'payment_status', label: 'Pay Status', sortable: true, width: 'w-[110px]' },
    { key: 'domains_count', label: 'Domains', sortable: false, width: 'w-[80px]' },
    { key: 'next_renewal_due', label: 'Next Renewal', sortable: false, width: 'w-[120px]' },
    { key: 'notes', label: 'Notes', sortable: false, width: 'w-[200px]' },
    { key: 'actions', label: '', sortable: false, width: 'w-[50px]' },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              {columns.map((col) => (
                <TableHead key={col.key} className={cn("h-11 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide", col.width)}>
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.key} className="px-4 py-3">
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
        <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1} />
        <h3 className="text-lg font-medium text-slate-900 mb-1">No clients found</h3>
        <p className="text-sm text-slate-500">Try adjusting your filters or add a new client.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <Table className="sticky-header" data-testid="clients-table">
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
              {columns.map((col) => (
                <TableHead 
                  key={col.key} 
                  className={cn(
                    "h-11 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap",
                    col.width,
                    col.sortable && "cursor-pointer select-none hover:text-slate-900"
                  )}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  <span className="flex items-center">
                    {col.label}
                    {col.sortable && <SortIcon field={col.key} sortBy={sortBy} sortOrder={sortOrder} />}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow 
                key={client.id} 
                className={cn(
                  "cursor-pointer table-row-hover group",
                  client.payment_status === 'Overdue' && "bg-red-50/30"
                )}
                onClick={() => onClientClick(client)}
                data-testid={`client-row-${client.id}`}
              >
                {/* Client Name */}
                <TableCell className="px-4 py-3 font-medium text-slate-900">
                  {editingCell?.clientId === client.id && editingCell?.field === 'client_name' ? (
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, client.id)}
                        className="h-7 text-sm inline-edit-input"
                        autoFocus
                      />
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => saveEdit(client.id, e)}>
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={cancelEdit}>
                        <X className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group/cell">
                      <span>{client.client_name}</span>
                      <button 
                        className="opacity-0 group-hover/cell:opacity-100 p-1 hover:bg-slate-100 rounded"
                        onClick={(e) => startEdit(client.id, 'client_name', client.client_name, e)}
                      >
                        <Edit2 className="h-3 w-3 text-slate-400" />
                      </button>
                    </div>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell className="px-4 py-3">
                  <Badge 
                    variant="outline" 
                    className={cn("text-xs font-medium", statusColors[client.status])}
                  >
                    {client.status}
                  </Badge>
                </TableCell>

                {/* Package */}
                <TableCell className="px-4 py-3">
                  <div className="flex flex-col">
                    <Badge variant="secondary" className="text-xs font-medium bg-indigo-50 text-indigo-700 border-indigo-200 w-fit">
                      {getPackageName(client)}
                    </Badge>
                    {client.mrr > 0 && (
                      <span className="text-xs text-slate-500 mt-0.5 tabular-nums">
                        £{client.mrr?.toFixed(2)}/mo
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Discount */}
                <TableCell className="px-4 py-3">
                  {client.discount_type !== 'None' && (
                    <Badge variant="outline" className="text-xs font-medium bg-amber-50 text-amber-700 border-amber-200">
                      {client.discount_type === '% Discount' ? `${client.discount_value}%` : 
                       client.discount_type === 'Fixed Discount' ? `£${client.discount_value}` : 
                       'Custom'}
                    </Badge>
                  )}
                </TableCell>

                {/* Payment Method */}
                <TableCell className="px-4 py-3 text-sm text-slate-600">
                  {client.payment_method}
                </TableCell>

                {/* Billing Frequency */}
                <TableCell className="px-4 py-3 text-sm text-slate-600">
                  {client.billing_frequency}
                </TableCell>

                {/* Payment Status */}
                <TableCell className="px-4 py-3">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-xs font-medium",
                      paymentStatusColors[client.payment_status],
                      client.payment_status === 'Overdue' && "pulse-warning"
                    )}
                  >
                    {client.payment_status === 'Overdue' && <AlertCircle className="w-3 h-3 mr-1" />}
                    {client.payment_status}
                  </Badge>
                </TableCell>

                {/* Domains Count */}
                <TableCell className="px-4 py-3 text-sm text-slate-600 tabular-nums">
                  {client.domains_count || 0}
                </TableCell>

                {/* Next Renewal */}
                <TableCell className="px-4 py-3">
                  {client.next_renewal_due ? (
                    <span className={cn(
                      "text-sm",
                      isRenewalSoon(client.next_renewal_due) ? "text-amber-600 font-medium" : "text-slate-600"
                    )}>
                      {formatDate(client.next_renewal_due)}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </TableCell>

                {/* Notes */}
                <TableCell className="px-4 py-3">
                  <span className="text-sm text-slate-500 truncate block max-w-[200px]" title={client.notes}>
                    {client.notes || '-'}
                  </span>
                </TableCell>

                {/* Actions */}
                <TableCell className="px-4 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 opacity-0 group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClientClick(client); }}>
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => startEdit(client.id, 'client_name', client.client_name, e)}>
                        Edit Name
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
